import React, { lazy, Suspense } from 'react';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import uk from 'dayjs/locale/uk';
import relativeTime from 'dayjs/plugin/relativeTime';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import QuestionsChevronIcon from 'assets/icons/questions_chevron.svg?react';
import QuoteIcon from 'assets/icons/flowbite_quote-solid.svg?react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LinearProgress from 'components/LinearProgress';
import { COLUMNS_LIMIT } from 'variables/main';
const ActionBlock = lazy(() => import('./ActionBlock'));
const QuestionChildWrapper = lazy(() => import('./QuestionChildWrapper'));

dayjs.extend(relativeTime);

const QuestionItem = styled('div')(({ theme, highlight }) => ({
  marginBottom: 25,
  gap: 30,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  maxWidth: '100%',
  position: 'relative',
  transition: '0.5s',
  ...(highlight === 'true'
    ? {
        backgroundColor: 'rgba(121, 201, 18, 0.1)',
        boxShadow: `0px 0px 10px 10px rgba(121, 201, 18, 0.1)`
      }
    : {}),
  [theme.breakpoints.down('md')]: {
    gap: 0
  }
}));

const WideSidebar = styled('div')(() => ({
  flex: 1
}));

const ShortSidebar = styled('div')(({ theme, answer }) => ({
  flex: 1,
  maxWidth: 120,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  borderLeft: answer === 'true' ? 'none' : '1px solid theme.palette.grey[300]',
  borderRight: answer === 'true' ? '1px solid theme.palette.grey[300]' : 'none',
  paddingLeft: answer === 'true' ? 0 : 15,
  paddingRight: answer === 'true' ? 15 : 0,
  [theme.breakpoints.down('md')]: {
    maxWidth: 95,
    paddingLeft: answer === 'true' ? 0 : 10,
    paddingRight: answer === 'true' ? 10 : 0,
    marginLeft: answer === 'true' ? 0 : 5,
    marginRight: answer === 'true' ? 5 : 0
  }
}));

const TimeAgo = styled(Typography)(({ answer }) => ({
  fontSize: 12,
  color: 'theme.palette.grey[300]',
  marginLeft: answer === 'true' ? 10 : 0
}));

const QuestionTitle = styled(Typography)(({ theme, type }) => ({
  fontSize: 16,
  lineHeight: '21px',
  color: theme.palette.grey[600],
  opacity: 0.8,
  fontWeight: 600,
  ...(type === 'advices'
    ? {
        color: theme.palette.white.main,
        fontSize: 18,
        opacity: 1,
        marginBottom: 10
      }
    : {}),
  [theme.breakpoints.down('md')]: {
    fontSize: 14
  }
}));

const QuestionAuthorPhoto = styled('img')(({ theme }) => ({
  width: 35,
  height: 35,
  borderRadius: '50%',
  objectFit: 'cover',
  [theme.breakpoints.down('md')]: {
    width: 30,
    height: 30
  }
}));

const DefaultUserPhoto = styled(AccountCircleIcon)(({ theme }) => ({
  width: 35,
  height: 35,
  borderRadius: '50%',
  objectFit: 'cover',
  color: theme.palette.grey[600],
  fill: theme.palette.grey[600],
  opacity: 0.8,
  [theme.breakpoints.down('md')]: {
    width: 30,
    height: 30
  }
}));

const QuestionAuthor = styled(Typography)(({ theme, type }) => ({
  fontSize: 14,
  lineHeight: '17px',
  fontWeight: 600,
  color: theme.palette.textPrimary.main,
  maxWidth: '100%',
  marginTop: 10,
  ...(type === 'advices'
    ? {
        color: theme.palette.white.main,
        fontSize: 14,
        marginBottom: 5
      }
    : {})
}));

const QuestionAuthorTitle = styled(Typography)(({ type, theme }) => ({
  lineHeight: '12px',
  marginTop: 5,
  fontSize: 12,
  maxWidth: '100%',
  color: 'theme.palette.grey[300]',
  ...(type === 'advices'
    ? {
        color: theme.palette.white.main,
        fontSize: 12
      }
    : {})
}));

const IconButtonStyled = styled(IconButton)(() => ({
  '& svg': {
    width: 20,
    height: 20
  }
}));

const IconButtonExpendStyled = styled(IconButtonStyled)(({ extend, theme }) => ({
  transform: extend === 'true' ? 'rotate(0deg)' : 'rotate(180deg)',
  position: 'relative',
  top: 37,
  '& svg': {
    [theme.breakpoints.down('md')]: {
      transform: 'scale(0.8)'
    }
  }
}));

const QuestionText = styled(Typography)(({ theme, marginTop, type }) => ({
  fontSize: 15,
  lineHeight: '23px',
  color: theme.palette.grey[600],
  opacity: 0.8,
  marginTop: marginTop || 0,
  ...(type === 'advices'
    ? {
        color: theme.palette.white.main,
        fontSize: 14,
        opacity: 1,
        marginBottom: 40
      }
    : {}),
  [theme.breakpoints.down('md')]: {
    fontSize: 14
  }
}));

const OutlinedButtonStyled = styled(Button)(() => ({
  display: 'block',
  margin: '0 auto',
  marginBottom: 16
}));

const AdvicesItem = styled('div')(() => ({
  background: 'linear-gradient(180deg, #74bf14 0%, #68a718 100%)',
  borderRadius: 15,
  padding: '16px 25px',
  '&:nth-child(odd)': {
    background: 'linear-gradient(180deg, #2eb0c4 0%, #2ab0b5 100%)'
  }
}));

const UserInfoWrapper = styled('div')(() => ({
  display: 'flex',
  marginBottom: 15,
  flexDirection: 'row',
  justifyContent: 'space-between'
}));

const FlexUserInfo = styled('div')(() => ({
  display: 'flex',
  gap: 10,
  alignItems: 'flex-end'
}));

const QuestionItemWrapper = ({
  anchor: anchorProp,
  entity,
  handleCheckAuth,
  handleGiveAnswer,
  item,
  readOnly,
  actions,
  index,
  type
}) => {
  const t = useTranslate('Questions');
  const location = useLocation();
  const anchor = location.hash.replace('#', '') || anchorProp;
  const [highlight, setHighlight] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [showAnswers, setShowAnswers] = React.useState(!!(anchor || readOnly || index === 0));

  React.useEffect(() => {
    if (anchor === item?._id) {
      setTimeout(() => {
        setHighlight(true);
        setTimeout(() => {
          setHighlight(false);
        }, 2500);
      }, 500);
    }
  }, [anchor, item?._id]);

  const getUserRole = React.useCallback(
    (roles) => {
      if (!(roles || []).length) return t('noRole');
      return roles.map((role) => t(role)).join(', ');
    },
    [t]
  );

  const handleLoadMore = React.useCallback(() => {
    setPage(page + 1);
  }, [page]);

  if (type === 'advices') {
    return (
      <AdvicesItem id={item?._id}>
        <UserInfoWrapper>
          <FlexUserInfo>
            {item?.userAvatar ? (
              <QuestionAuthorPhoto src={item?.userAvatar} alt="author photo" />
            ) : (
              <DefaultUserPhoto />
            )}
            <span>
              <QuestionAuthor type={type}>{item?.userName}</QuestionAuthor>
              <QuestionAuthorTitle type={type}>
                {getUserRole(item?.userStatuses)}
              </QuestionAuthorTitle>
            </span>
          </FlexUserInfo>
          <QuoteIcon />
        </UserInfoWrapper>

        <QuestionTitle type={type}>{item?.title}</QuestionTitle>
        <QuestionText type={type}>{item?.text}</QuestionText>

        <ActionBlock
          _id={item?._id}
          type={type}
          entity={entity}
          readOnly={readOnly}
          isFavorite={item?.isFavorite}
          votesTotal={item?.votesTotal}
          handleCheckAuth={handleCheckAuth}
          handleGiveAnswer={handleGiveAnswer}
          actions={actions}
          answers={(item.answers || []).length}
        />
      </AdvicesItem>
    );
  }

  return (
    <>
      <QuestionItem id={item?._id} highlight={`${highlight}`}>
        <WideSidebar>
          <TimeAgo>{dayjs(item?.createdAt).locale(uk).fromNow()}</TimeAgo>

          {item?.title ? (
            <>
              <QuestionTitle>{item?.title}</QuestionTitle>
              <QuestionText marginTop={'10px'}>{item?.text}</QuestionText>
            </>
          ) : (
            <QuestionTitle>{item?.text}</QuestionTitle>
          )}

          <ActionBlock
            _id={item?._id}
            entity={entity}
            readOnly={readOnly}
            isFavorite={item?.isFavorite}
            votesTotal={item?.votesTotal}
            handleCheckAuth={handleCheckAuth}
            handleGiveAnswer={handleGiveAnswer}
            actions={actions}
            answers={(item.answers || []).length}
          />
        </WideSidebar>

        {(item.answers || []).length > 0 ? (
          <IconButtonExpendStyled
            extend={`${showAnswers}`}
            onClick={() => setShowAnswers(!showAnswers)}>
            <QuestionsChevronIcon />
          </IconButtonExpendStyled>
        ) : null}

        <ShortSidebar>
          {item?.userAvatar ? (
            <QuestionAuthorPhoto src={item?.userAvatar} alt="author photo" />
          ) : (
            <DefaultUserPhoto />
          )}

          <QuestionAuthor>{item?.userName}</QuestionAuthor>
          <QuestionAuthorTitle>{getUserRole(item?.userStatuses)}</QuestionAuthorTitle>
        </ShortSidebar>
      </QuestionItem>

      {showAnswers ? (
        <Suspense fallback={<LinearProgress />}>
          {(item.answers || []).map((child, i) => {
            const hidden = i >= COLUMNS_LIMIT * page && !anchor;

            if (hidden) return null;

            return (
              <QuestionChildWrapper
                key={child?._id}
                child={child}
                entity={entity}
                readOnly={readOnly}
                anchor={anchorProp}
                getUserRole={getUserRole}
                handleCheckAuth={handleCheckAuth}
                handleGiveAnswer={handleGiveAnswer}
                actions={actions}
              />
            );
          })}

          {item?.answers?.length > COLUMNS_LIMIT * page ? (
            <OutlinedButtonStyled size={'small'} variant="outlined" onClick={handleLoadMore}>
              {t('MoreAnswers')}
            </OutlinedButtonStyled>
          ) : null}
        </Suspense>
      ) : null}
    </>
  );
};

export default QuestionItemWrapper;
